import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Save, Video, Clock, FileVideo } from 'lucide-react';
import Button from './Button';
import toast from 'react-hot-toast';

interface SessionFormData {
  title: string;
  description: string;
  notes: string;
}

interface PreRecordingData {
  phobiaType?: string;
  exposureLevel?: number;
  triggers?: string[];
  sessionGoals?: string;
}

interface PostRecordingFormProps {
  recordingBlob: Blob | null;
  recordingDuration: number;
  onSaveSession: (data: SessionFormData, videoBlob: Blob) => Promise<void>;
  onCancel: () => void;
  preRecordingData?: PreRecordingData | null;
}

const PostRecordingForm: React.FC<PostRecordingFormProps> = ({
  recordingBlob,
  recordingDuration,
  onSaveSession,
  onCancel,
  preRecordingData
}) => {
  const [isSaving, setIsSaving] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SessionFormData>({
    defaultValues: {
      title: '',
      description: '',
      notes: '',
    },
  });

  const onSubmit = async (data: SessionFormData) => {
    if (isSaving) return; // Prevent multiple submissions
    
    console.log('PostRecordingForm onSubmit - recordingBlob:', recordingBlob);
    console.log('PostRecordingForm onSubmit - blob size:', recordingBlob?.size);
    console.log('PostRecordingForm onSubmit - blob type:', recordingBlob?.type);
    console.log('PostRecordingForm onSubmit - recordingDuration:', recordingDuration, 'seconds');
    
    if (!recordingBlob) {
      console.error('No recording blob available');
      toast.error('No recording found. Please record a video first.');
      return;
    }
    
    if (recordingBlob.size === 0) {
      console.error('Recording blob is empty');
      toast.error('Recording appears to be empty. Please record a video first.');
      return;
    }
    
    try {
      setIsSaving(true);
      console.log('Calling onSaveSession with data:', data);
      await onSaveSession(data, recordingBlob);
      // Don't show success toast here - let the parent component handle it
    } catch (error) {
      toast.error('Failed to save video');
      console.error('Save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    if (mb < 1) {
      return `${(mb * 1024).toFixed(0)} KB`;
    }
    return `${mb.toFixed(1)} MB`;
  };


  return (
    <div className="max-w-4xl mx-auto p-6 overflow-visible">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Complete Your Video</h1>
        <p className="mt-2 text-sm sm:text-base text-gray-600">Add details about your recorded ETR exposure video.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Recording Summary */}
        <div className="md:col-span-1">
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-gray-900">Recording Summary</h2>
            </div>
            <div className="card-content">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Video className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Recording Complete</p>
                    <p className="text-xs text-gray-500">Ready to save</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Clock className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Duration</p>
                    <p className="text-xs text-gray-500">{formatDuration(recordingDuration)}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <FileVideo className="h-5 w-5 text-purple-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">File Size</p>
                    <p className="text-xs text-gray-500">
                      {recordingBlob ? formatFileSize(recordingBlob.size) : 'Unknown'}
                    </p>
                  </div>
                </div>
              </div>
              
              {recordingBlob && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    ✅ Recording successfully captured and ready to save
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Video Form */}
        <div className="md:col-span-2">
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-gray-900">Video Details</h2>
            </div>
            <div className="card-content">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                    Video Title *
                  </label>
                  <input
                    {...register('title', { required: 'Title is required' })}
                    type="text"
                    className="input"
                    placeholder="Enter video title"
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="input"
                    placeholder="Describe the video goals and approach"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                    Video Notes (Optional)
                  </label>
                  <textarea
                    {...register('notes')}
                    rows={4}
                    className="input"
                    placeholder="How did the video go? What did you learn? Any observations?"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="md"
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                  
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    isLoading={isSaving}
                    leftIcon={!isSaving ? <Save className="h-4 w-4" /> : undefined}
                  >
                    {isSaving ? 'Saving...' : 'Save Video'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostRecordingForm;
